import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderPlus, FolderOpen, Github, FileArchive, X } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';

export default function AddProjectDialog() {
  const isOpen = useUIStore((state) => state.addProjectDialogOpen);
  const close = useUIStore((state) => state.closeAddProjectDialog);
  const navigate = useNavigate();

  // Step any CEF child webviews out of the way while this dialog is open.
  useEffect(() => {
    if (!isOpen) return;
    const { pushOverlay, popOverlay } = useUIStore.getState();
    pushOverlay();
    return () => popOverlay();
  }, [isOpen]);

  if (!isOpen) return null;

  const go = (mode: string) => {
    close();
    navigate(`/wizard?mode=${mode}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-base-300">
          <h2 className="text-lg font-bold">Add Project</h2>
          <button onClick={close} className="btn btn-ghost btn-sm btn-circle">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-3">
          <button
            onClick={() => go('new')}
            className="w-full flex items-center gap-4 p-4 rounded-lg bg-base-200 hover:bg-base-300 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FolderPlus className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-medium">Create New Project</div>
              <div className="text-sm text-base-content/60">Set up a new project from scratch</div>
            </div>
          </button>

          <button
            onClick={() => go('existing')}
            className="w-full flex items-center gap-4 p-4 rounded-lg bg-base-200 hover:bg-base-300 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <FolderOpen className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <div className="font-medium">Add Existing Project</div>
              <div className="text-sm text-base-content/60">Import a project from an existing directory</div>
            </div>
          </button>

          <button
            onClick={() => go('github')}
            className="w-full flex items-center gap-4 p-4 rounded-lg bg-base-200 hover:bg-base-300 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Github className="w-5 h-5 text-accent" />
            </div>
            <div>
              <div className="font-medium">Clone from GitHub</div>
              <div className="text-sm text-base-content/60">Pick a repo, we'll pull it and set it up</div>
            </div>
          </button>

          <button
            onClick={() => go('zip')}
            className="w-full flex items-center gap-4 p-4 rounded-lg bg-base-200 hover:bg-base-300 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center flex-shrink-0">
              <FileArchive className="w-5 h-5 text-info" />
            </div>
            <div>
              <div className="font-medium">Import from ZIP</div>
              <div className="text-sm text-base-content/60">Extract a .zip and set it up as a project</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
